import { useEffect, useState } from 'react'
import AsyncStorage from '@react-native-async-storage/async-storage'
import * as Sentry from '@sentry/react-native'

const LAST_LOGIN_METHOD_KEY = 'lastLoginMethod'
export type LoginMethod = 'google' | 'wechat' | 'otp'

const isValidLoginMethod = (
  loginMethod: string | null
): loginMethod is LoginMethod =>
  loginMethod === 'google' || loginMethod === 'wechat' || loginMethod === 'otp'

let sharedLastLoginMethod: LoginMethod | undefined
const subscribers = new Set<(method: LoginMethod) => void>()

export const useLastLoginMethod = () => {
  const [lastLoginMethod, setLastLoginMethodState] = useState<
    LoginMethod | undefined
  >(sharedLastLoginMethod)

  useEffect(() => {
    // Each hook subscribe to get shared "memory"
    const onMethodUpdated = (method: LoginMethod) => {
      setLastLoginMethodState(method)
    }
    subscribers.add(onMethodUpdated)

    if (sharedLastLoginMethod) {
      return () => {
        subscribers.delete(onMethodUpdated)
      }
    }

    AsyncStorage.getItem(LAST_LOGIN_METHOD_KEY)
      .then((storedLoginMethod) => {
        if (!isValidLoginMethod(storedLoginMethod)) {
          return
        }
        sharedLastLoginMethod = storedLoginMethod
        setLastLoginMethodState(storedLoginMethod)
      })
      .catch((error) => {
        console.error(error)
        Sentry.captureException(error)
      })

    return () => {
      subscribers.delete(onMethodUpdated)
    }
  }, [])

  const setLastLoginMethod = async (method: LoginMethod) => {
    sharedLastLoginMethod = method
    setLastLoginMethodState(method)
    subscribers.forEach((notify) => notify(method))
    try {
      await AsyncStorage.setItem(LAST_LOGIN_METHOD_KEY, method)
    } catch (error) {
      console.error(error)
      Sentry.captureException(error)
    }
  }

  return {
    lastLoginMethod,
    setLastLoginMethod,
  }
}
